import React, { useState, useEffect } from 'react';
import { Zap, ArrowRight, DollarSign, ShieldCheck, CheckCircle2, Clock, Loader2, PenLine, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { SignaturePad } from '@/components/SignaturePad';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

// Drawable balance for a line item: what's spent (or estimated) minus already drawn.
function drawable(p) {
  const base = (parseFloat(p.actual_cost) || 0) > 0 ? parseFloat(p.actual_cost) : (parseFloat(p.estimated_cost) || 0);
  const drawn = parseFloat(p.amount_drawn) || 0;
  return Math.max(0, base - drawn);
}

/**
 * QuickDrawModal — the fast, single-item draw path (v2.9.11).
 * Pick one line item → amount pre-filled → compliance docs auto-attached
 * (bundled by the backend ZIP) → submit. Creates a tracked pending draw so the
 * 24h deposit-confirmation nudge fires. Lives on Budget & Progress.
 */
export default function QuickDrawModal({ projectId, userId, phases = [], formStyle = 'hills', onComplete, open: openProp, onOpenChange, preselectId = null, hideTrigger = false }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = onOpenChange || setOpenState;
  const [selectedId, setSelectedId] = useState(null);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  const [drawnSignature, setDrawnSignature] = useState(null);
  const effectiveSignature = drawnSignature || savedSignature;

  const eligible = (phases || []).filter((p) => drawable(p) > 0);
  const item = eligible.find((p) => p.id === selectedId) || null;
  const isSmall = item && (parseFloat(item.actual_cost) || parseFloat(item.estimated_cost) || 0) < 2500;

  const pick = (p) => { setSelectedId(p.id); setAmount(String(drawable(p).toFixed(2))); };
  const openDialog = () => { setSelectedId(null); setAmount(''); setOpen(true); };

  // When opened with a preselected line (per-line "Quick Draw" button), jump
  // straight to the amount + submit for that item — no picker.
  useEffect(() => {
    if (open && preselectId) {
      const p = (phases || []).find((x) => x.id === preselectId);
      if (p) pick(p);
    }
    if (!open) { setSelectedId(null); setAmount(''); setDrawnSignature(null); }
  }, [open, preselectId]);

  // Load the user's saved signature so we can stamp it on the draw form.
  useEffect(() => {
    if (open && userId) {
      apiService.getSignature(userId)
        .then((r) => setSavedSignature(r?.data?.signature_image || null))
        .catch(() => setSavedSignature(null));
    }
  }, [open, userId]);

  const submit = async () => {
    const amt = parseFloat(amount) || 0;
    if (!item || amt <= 0) { toast.error('Pick an item and a draw amount'); return; }
    if (!effectiveSignature) { toast.error('Add your signature to sign the draw request'); return; }
    setSubmitting(true);
    try {
      // If the user drew a new signature, save it so the backend stamps it on the form.
      if (drawnSignature && drawnSignature !== savedSignature && userId) {
        try { await apiService.uploadSignature(userId, drawnSignature); setSavedSignature(drawnSignature); }
        catch (_) { /* non-fatal — proceed with generation */ }
      }
      const res = await apiService.generateReimbursementRequest({
        project_id: projectId,
        phase_ids: [item.id],
        phase_amounts: { [item.id]: amt },
        expense_ids: [],
        doc_format: 'pdf',
        form_style: formStyle,
        single_file: false,   // ZIP: bundles the form + auto-attached compliance docs, and records a pending draw
        sign_document: true,  // stamp the user's signature + today's date onto the form
        user_id: userId,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `QuickDraw_${item.name}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Draw request submitted! We'll remind you in 24h to confirm the deposit.");
      setOpen(false);
      if (onComplete) onComplete();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && /loan_number|draw_account|short_legal/i.test(JSON.stringify(detail))) {
        toast.error('Add your loan info (Loan #, Draw Account, Legal Description) via Edit Loan Details first.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not generate the draw request');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          onClick={openDialog}
          data-testid="quick-draw-cta"
          className="mt-2 w-full group inline-flex items-center justify-between gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold text-sm px-4 py-2.5 shadow-sm transition-colors"
        >
          <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4" /> Quick Draw</span>
          <span className="text-[11px] font-normal text-cyan-50/90 inline-flex items-center gap-1">one item · ~2 clicks <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-blue-600" /> Quick Draw</DialogTitle>
            <DialogDescription>Pick the item you're drawing for. We'll pre-fill the amount and auto-attach its compliance docs.</DialogDescription>
          </DialogHeader>

          {eligible.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No line items with a drawable balance yet. Log costs or payments first, then come back.</p>
          ) : (preselectId && !item) ? (
            <p className="text-sm text-slate-500 py-6 text-center">This line has nothing left to draw right now.</p>
          ) : (
            <div className="space-y-3">
              {!preselectId && (
                <>
                  <p className="text-xs font-semibold text-slate-600">What are you drawing for?</p>
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {eligible.map((p) => {
                      const active = selectedId === p.id;
                      return (
                        <button
                          key={p.id}
                          onClick={() => pick(p)}
                          data-testid={`quick-draw-item-${p.id}`}
                          className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${active ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-slate-800">{p.name}</p>
                              <p className="text-[11px] text-slate-500">{p.category || p.phase_type || 'Line item'}</p>
                            </div>
                            <p className="font-semibold text-slate-700">{money(drawable(p))}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {preselectId && item && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{item.name}</p>
                      <p className="text-[11px] text-slate-500">{item.category || item.phase_type || 'Line item'}</p>
                    </div>
                    <p className="font-semibold text-slate-700">{money(drawable(item))}</p>
                  </div>
                </div>
              )}

              {item && (
                <div className="space-y-4 pt-1">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Draw amount</label>
                    <div className="relative mt-1">
                      <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        data-testid="quick-draw-amount"
                        className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                      />
                    </div>
                  </div>

                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                    <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Compliance docs auto-attached</p>
                    <p className="text-[11px] text-emerald-700/80 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {isSmall ? 'Invoice attached · COI/Lien auto-skipped (under $2,500)' : "We'll bundle the invoice, COI, and lien waiver on file for this item — no selecting."}
                    </p>
                  </div>

                  {/* Signature & date — every quick draw is signed and dated */}
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-600 flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> Signature</p>
                      <span className="text-[11px] text-slate-500 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Date: {today()}</span>
                    </div>
                    {savedSignature && !drawnSignature ? (
                      <div className="flex items-center gap-3">
                        <img src={savedSignature} alt="Your signature" className="h-12 border border-slate-200 rounded bg-white px-2" data-testid="quick-draw-saved-sig" />
                        <button type="button" onClick={() => setSavedSignature(null)} className="text-[11px] font-semibold text-blue-600 hover:text-blue-700" data-testid="quick-draw-resign">Draw a new one</button>
                      </div>
                    ) : (
                      <div>
                        <SignaturePad width={360} height={110} onSave={setDrawnSignature} onClear={() => setDrawnSignature(null)} />
                        {drawnSignature && <p className="mt-1 text-[11px] text-green-600 flex items-center gap-1" data-testid="quick-draw-sig-ready"><CheckCircle2 className="w-3 h-3" /> Signature captured</p>}
                        <p className="mt-1 text-[10px] text-slate-400">Saving a new signature updates your default signature for future draws.</p>
                      </div>
                    )}
                  </div>

                  <Button onClick={submit} disabled={submitting || !effectiveSignature} className="w-full" data-testid="quick-draw-submit">
                    {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                    Submit Draw Request
                  </Button>
                  <p className="text-[11px] text-center text-slate-400 flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" /> We'll remind you in 24 hours to confirm the deposit.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
